import { useState, useEffect, useRef, useCallback } from "react";
import { useApp, api } from "../context";

// ── Sound Engine ──
const playSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const g = ctx.createGain();
    g.connect(ctx.destination);
    const o = ctx.createOscillator();
    o.connect(g);
    const now = ctx.currentTime;
    switch(type) {
      case "pick":
        o.frequency.setValueAtTime(520, now);
        o.frequency.exponentialRampToValueAtTime(640, now + 0.06);
        g.gain.setValueAtTime(0.18, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        o.start(now); o.stop(now + 0.08); break;
      case "place":
        o.type = "sine";
        o.frequency.setValueAtTime(300, now);
        o.frequency.exponentialRampToValueAtTime(180, now + 0.12);
        g.gain.setValueAtTime(0.22, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
        o.start(now); o.stop(now + 0.14); break;
      case "win":
        [0,0.12,0.24,0.38].forEach((t,i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.frequency.value = [523,659,784,1047][i];
          gain.gain.setValueAtTime(0.28, now+t);
          gain.gain.exponentialRampToValueAtTime(0.001, now+t+0.25);
          osc.start(now+t); osc.stop(now+t+0.25);
        }); break;
      case "lose":
        o.type = "sawtooth";
        o.frequency.setValueAtTime(400, now);
        o.frequency.exponentialRampToValueAtTime(150, now + 0.5);
        g.gain.setValueAtTime(0.2, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
        o.start(now); o.stop(now + 0.5); break;
      default: break;
    }
  } catch(e) {}
};

const PALETTE = [
  ["#6fc7ff","#1473e6"],["#ff8a8a","#d31222"],["#8aff8a","#15a31f"],
  ["#fff3a0","#e6c411"],["#ffc07d","#e06400"],["#ffb1d8","#e8559b"],
  ["#c9a7ff","#6a2fd6"],["#7df3e1","#0e9c8a"],
];

function mulberry32(seed) {
  return function() {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateTubes(numColors, seed, cap = 4) {
  const rng = mulberry32(seed);
  const balls = [];
  for (let c = 0; c < numColors; c++) for (let i = 0; i < cap; i++) balls.push(c);
  for (let i = balls.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [balls[i], balls[j]] = [balls[j], balls[i]];
  }
  const tubes = [];
  for (let t = 0; t < numColors; t++) tubes.push(balls.slice(t * cap, (t + 1) * cap));
  tubes.push([], []);
  const solved = tubes.every(t => t.length === 0 || (t.length === cap && t.every(b => b === t[0])));
  return solved ? generateTubes(numColors, seed + 1, cap) : tubes;
}

const isTubeComplete = (t, cap = 4) => t.length === cap && t.every(b => b === t[0]);
const isBoardSolved = (tubes, cap = 4) => tubes.every(t => t.length === 0 || isTubeComplete(t, cap));

const ROUND_COLORS = (round) => {
  if (round <= 2) return { colors: 4, time: 90, cap: 4 };
  if (round <= 4) return { colors: 5, time: 75, cap: 4 };
  return { colors: 6, time: 60, cap: 4 };
};

function Confetti() {
  const pieces = Array.from({ length: 48 }, (_, i) => i);
  return (
    <div className="bs-confetti" aria-hidden="true">
      {pieces.map((i) => {
        const [hi] = PALETTE[i % PALETTE.length];
        return <i key={i} style={{
          left: `${(i * (7 + i % 13)) % 100}%`,
          background: i % 5 === 0 ? "#ffd34d" : hi,
          animationDelay: `${(i % 12) * 0.12}s`,
          animationDuration: `${2.2 + (i % 7) * 0.25}s`,
          width: i % 3 === 0 ? "6px" : "9px",
          height: i % 4 === 0 ? "14px" : "9px",
          borderRadius: i % 2 ? "50%" : "2px",
        }} />;
      })}
    </div>
  );
}

function DuoLeaderboard({ userId }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback((p = 1) => {
    setLoading(true);
    api(`/duo-ball-sort/leaderboard?page=${p}`).then(r => {
      if (r.ok) {
        setLeaderboard(r.data.leaderboard || []);
        setMyRank(r.data.my_rank);
        setTotalPages(r.data.total_pages || 1);
        setPage(p);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(1); }, []); // eslint-disable-line

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#35c4ff", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        🏆 Ball Sort Duo Leaderboard
        {myRank && <span style={{ fontSize: 11, color: "#8d92a3", fontWeight: 600 }}>You're #{myRank}</span>}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "20px 0", color: "#8d92a3", fontSize: 13 }}>Loading...</div>
      ) : leaderboard.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px 0", color: "#8d92a3", fontSize: 13 }}>No games completed yet — be the first!</div>
      ) : leaderboard.map((player) => {
        const isMe = player.id === userId;
        const rank = player.rank;
        const crown = rank === 1 ? "👑" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
        return (
          <div key={player.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, marginBottom: 6, background: isMe ? "rgba(53,196,255,0.1)" : "rgba(255,255,255,0.03)", border: `1px solid ${isMe ? "rgba(53,196,255,0.3)" : "rgba(255,255,255,0.06)"}` }}>
            <span style={{ fontSize: crown ? 18 : 13, fontWeight: 800, color: isMe ? "#35c4ff" : "#8d92a3", minWidth: 28, textAlign: "center" }}>
              {crown || `#${rank}`}
            </span>
            <div style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, overflow: "hidden", background: "linear-gradient(135deg,#35c4ff,#1473e6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
              {player.avatar_url
                ? <img src={player.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : (player.avatar_emoji || player.name?.[0] || "?")}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: isMe ? "#35c4ff" : "#f2f3f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {isMe ? "You" : player.name}
                {player.is_verified && <span style={{ fontSize: 10, color: "#3b82f6", marginLeft: 4 }}>✓</span>}
              </div>
              <div style={{ fontSize: 11, color: "#8d92a3" }}>
                🏆 {player.wins} wins · {player.win_rate}% win rate
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#ffd34d" }}>{player.wins}</div>
              <div style={{ fontSize: 10, color: "#8d92a3" }}>wins</div>
            </div>
          </div>
        );
      })}

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
          <button onClick={() => load(page - 1)} disabled={page <= 1}
            style={{ padding: "8px 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: page <= 1 ? "#555" : "#f2f3f7", fontSize: 12, fontWeight: 700, cursor: page <= 1 ? "default" : "pointer", fontFamily: "inherit" }}>← Prev</button>
          <span style={{ padding: "8px 12px", fontSize: 12, color: "#8d92a3", fontWeight: 700 }}>{page} / {totalPages}</span>
          <button onClick={() => load(page + 1)} disabled={page >= totalPages}
            style={{ padding: "8px 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.07)", color: page >= totalPages ? "#555" : "#f2f3f7", fontSize: 12, fontWeight: 700, cursor: page >= totalPages ? "default" : "pointer", fontFamily: "inherit" }}>Next →</button>
        </div>
      )}

      </div>
  );
}

export default function DuoBallSortPage({ game, onGameEnd, onRematch }) {
  const { user, go } = useApp();
  const [tubes, setTubes] = useState([]);
  const [selected, setSelected] = useState(-1);
  const [moves, setMoves] = useState(0);
  const [timeLeft, setTimeLeft] = useState(90);
  const [phase, setPhase] = useState("playing");
  const [opponentProgress, setOpponentProgress] = useState(0);
  const [myProgress, setMyProgress] = useState(0);
  const [roundResult, setRoundResult] = useState(null);
  const [scores, setScores] = useState({ me: 0, opp: 0 });
  const [currentRound, setCurrentRound] = useState(1);
  const [totalRounds] = useState(game?.total_rounds || game?.rounds || 5);
  const [gameOver, setGameOver] = useState(null);
  const [lastDrop, setLastDrop] = useState(null);

  const historyRef = useRef([]);
  const pollRef = useRef(null);
  const submittedRef = useRef(false);
  const roundResultShownRef = useRef(new Set());
  const gameOverShownRef = useRef(false);
  const currentRoundRef = useRef(1);
  const phaseRef = useRef("playing");

  const isMe = user?.id === game?.player1_id ? "player1" : "player2";
  const myId = isMe === "player1" ? game?.player1_id : game?.player2_id;
  const oppName = isMe === "player1" ? game?.player2_name : game?.player1_name;
  const oppAvatar = isMe === "player1" ? game?.player2_avatar : game?.player1_avatar;
  const oppEmoji = isMe === "player1" ? game?.player2_emoji : game?.player1_emoji;

  const config = ROUND_COLORS(currentRound);

  useEffect(() => { currentRoundRef.current = currentRound; }, [currentRound]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const startRound = useCallback((round, seed) => {
    const cfg = ROUND_COLORS(round);
    setTubes(generateTubes(cfg.colors, seed, cfg.cap));
    setSelected(-1);
    setMoves(0);
    setTimeLeft(cfg.time);
    setLastDrop(null);
    setPhase("playing");
    setMyProgress(0);
    setOpponentProgress(0);
    submittedRef.current = false;
    historyRef.current = [];
    currentRoundRef.current = round;
    phaseRef.current = "playing";
  }, []);

  // Start first round
  useEffect(() => {
    if (game?.seed) startRound(1, game.seed);
  }, []); // eslint-disable-line

  // Timer
  useEffect(() => {
    if (phase !== "playing") return;
    if (timeLeft <= 0) { handleTimeout(); return; }
    const id = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, timeLeft]); // eslint-disable-line

  // ── GLOBAL POLL ──
  useEffect(() => {
    if (!game?.id) return;

    const poll = async () => {
      const r = await api(`/duo-ball-sort/${game.id}/state`);
      if (!r.ok) return;
      const data = r.data;

      // Always update opponent progress
      const oppProg = isMe === "player1" ? data.player2_progress : data.player1_progress;
      setOpponentProgress(oppProg || 0);

      // ── GAME OVER ──
      if (data.status === "completed" && !gameOverShownRef.current) {
        gameOverShownRef.current = true;
        clearInterval(pollRef.current);
        const myScore = isMe === "player1" ? data.player1_score : data.player2_score;
        const oppScore = isMe === "player1" ? data.player2_score : data.player1_score;
        setScores({ me: myScore, opp: oppScore });

        if (data.last_round_winner && data.last_round_number) {
          const roundKey = `last_${data.last_round_number}`;
          if (!roundResultShownRef.current.has(roundKey)) {
            roundResultShownRef.current.add(roundKey);
            const iWon = String(data.last_round_winner) === String(myId);
            setRoundResult({ winner: iWon ? "me" : "opp", myScore, oppScore });
            setPhase("done");
            setTimeout(() => {
              setRoundResult(null);
              setGameOver(data);
              if (onGameEnd) onGameEnd(data);
            }, 3000);
            return;
          }
        }
        setPhase("done");
        setTimeout(() => {
          setGameOver(data);
          if (onGameEnd) onGameEnd(data);
        }, 500);
        return;
      }

      // ── ROUND ENDED ──
      if (data.last_round_winner && data.last_round_number && data.status === "active") {
        const roundKey = `last_${data.last_round_number}`;
        if (!roundResultShownRef.current.has(roundKey)) {
          roundResultShownRef.current.add(roundKey);
          clearInterval(pollRef.current);

          const myScore = isMe === "player1" ? data.player1_score : data.player2_score;
          const oppScore = isMe === "player1" ? data.player2_score : data.player1_score;
          const iWon = String(data.last_round_winner) === String(myId);

          setRoundResult({ winner: iWon ? "me" : "opp", myScore, oppScore });
          setScores({ me: myScore, opp: oppScore });
          setPhase("done");

          setTimeout(() => {
            setRoundResult(null);
            const nextRound = data.current_round;
            const nextSeed = data.seed;
            setCurrentRound(nextRound);
            startRound(nextRound, nextSeed);
            pollRef.current = setInterval(poll, 2000);
          }, 3000);
        }
      }
    };

    pollRef.current = setInterval(poll, 2000);
    poll();
    return () => clearInterval(pollRef.current);
  }, [game?.id, isMe, myId]); // eslint-disable-line

  const calcProgress = (tubesList, cap) => {
    const colorTubes = tubesList.slice(0, tubesList.length - 2);
    const done = colorTubes.filter(t => isTubeComplete(t, cap)).length;
    return Math.round((done / colorTubes.length) * 100);
  };

  const submitProgress = async (tubesList, won) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const prog = calcProgress(tubesList, config.cap);
    await api(`/duo-ball-sort/${game?.id}/move`, {
      method: "POST",
      body: JSON.stringify({
        progress: prog,
        moves,
        won,
        round: currentRoundRef.current,
        time_left: timeLeft,
      }),
    });
    if (won) setPhase("waiting");
  };

  const handleTap = (idx) => {
    if (phase !== "playing") return;
    setLastDrop(null);
    if (selected === -1) {
      if (tubes[idx].length === 0) return;
      playSound("pick");
      setSelected(idx); return;
    }
    if (selected === idx) { setSelected(-1); return; }
    const src = tubes[selected];
    const dst = tubes[idx];
    const legal = src.length > 0 && dst.length < config.cap;
    if (!legal) { setSelected(tubes[idx].length ? idx : -1); return; }
    historyRef.current.push(tubes.map(t => t.slice()));
    const next = tubes.map(t => t.slice());
    next[idx].push(next[selected].pop());
    setTubes(next);
    setSelected(-1);
    setMoves(m => m + 1);
    playSound("place");
    setLastDrop({ tube: idx });
    const prog = calcProgress(next, config.cap);
    setMyProgress(prog);
    if (isBoardSolved(next, config.cap)) {
      playSound("win");
      submitProgress(next, true);
    }
  };

  const handleTimeout = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setPhase("waiting");
    phaseRef.current = "waiting";
    const prog = calcProgress(tubes, config.cap);
    await api(`/duo-ball-sort/${game?.id}/move`, {
      method: "POST",
      body: JSON.stringify({
        progress: prog,
        moves,
        won: false,
        round: currentRoundRef.current,
        time_left: 0,
      }),
    });
  };

  const handleUndo = () => {
    if (phase !== "playing" || !historyRef.current.length) return;
    setTubes(historyRef.current.pop());
    setSelected(-1); setLastDrop(null);
    setMoves(m => m + 1);
  };

  const timeRatio = Math.max(0, Math.min(1, timeLeft / (config.time || 1)));
  const lowTime = timeRatio <= 0.25;

  return (
    <div className="bs-stage">
      <style>{CSS_TEXT}</style>
      <div className="bs-shell">

        <button onClick={() => go("duo_play")} style={{ alignSelf: "flex-start", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, color: "#f2f3f7", padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 4 }}>← Back</button>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(255,255,255,0.05)", borderRadius: 16, padding: "10px 16px", border: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#35c4ff", fontWeight: 800, marginBottom: 2 }}>YOU</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#35c4ff" }}>{scores.me}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 10, color: "#8d92a3", fontWeight: 700 }}>ROUND {currentRound}/{totalRounds}</div>
            <div style={{ fontSize: 13, color: "#ffd34d", fontWeight: 800 }}>🎯 First to {Math.ceil(totalRounds/2)} wins</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#ff6fb0", fontWeight: 800, marginBottom: 2 }}>{oppName?.split(" ")[0]?.toUpperCase()}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#ff6fb0" }}>{scores.opp}</div>
          </div>
        </div>

        <div style={{ background: "rgba(255,107,176,0.08)", borderRadius: 12, padding: "10px 14px", border: "1px solid rgba(255,107,176,0.2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#ff6fb0,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
              {oppAvatar ? <img src={oppAvatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} /> : (oppEmoji || oppName?.[0] || "?")}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#ff6fb0" }}>{oppName} — {opponentProgress}% done</div>
              <div style={{ height: 6, borderRadius: 99, background: "rgba(255,255,255,0.1)", marginTop: 4, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${opponentProgress}%`, background: "linear-gradient(90deg,#ff6fb0,#e91e8c)", borderRadius: 99, transition: "width 0.5s ease" }} />
              </div>
            </div>
          </div>
        </div>

        <header className="bs-top">
          <div className="bs-lives" style={{ fontSize: 12, color: "#35c4ff", fontWeight: 800 }}>You — {myProgress}%</div>
          <div className="bs-level">
            <span className="bs-level-eyebrow">ROUND</span>
            <span className="bs-level-num">{currentRound}</span>
          </div>
          <div className={"bs-clock" + (lowTime ? " low" : "")}>⏱ {Math.max(0, timeLeft)}s</div>
        </header>

        <div className="bs-timer">
          <i className={lowTime ? "low" : ""} style={{ width: `${timeRatio * 100}%` }} />
        </div>

        <main className="bs-board">
          {tubes.map((tube, ti) => {
            const complete = isTubeComplete(tube, config.cap);
            const sel = ti === selected;
            return (
              <button key={ti} className={"bs-tube" + (sel ? " sel" : "") + (complete ? " done" : "")}
                style={{ '--cap': config.cap }}
                onClick={() => phase === "playing" && handleTap(ti)}>
                <span className="bs-rim" />
                {tube.map((color, bi) => {
                  const [hi, lo] = PALETTE[color % PALETTE.length];
                  const lifted = sel && bi === tube.length - 1;
                  const dropping = lastDrop?.tube === ti && bi === tube.length - 1;
                  return (
                    <span key={bi} className={"bs-ball" + (lifted ? " lift" : "") + (dropping ? " drop" : "")}
                      style={{ background: `radial-gradient(circle at 32% 28%, ${hi}, ${lo} 72%)` }} />
                  );
                })}
              </button>
            );
          })}
        </main>

        <footer className="bs-bottom">
          <button className="bs-undo" onClick={handleUndo} disabled={phase !== "playing"}>↩ Undo</button>
          <div className="bs-meta">
            <span className="bs-moves">Moves {moves}</span>
            {phase === "waiting" && <span style={{ color: "#ffd34d", fontSize: 12, fontWeight: 700 }}>⏳ Waiting for opponent...</span>}
          </div>
        </footer>

        <DuoLeaderboard userId={user?.id} />

        <div style={{ height: 80 }} />
      </div>

      {/* ROUND RESULT OVERLAY */}
      {roundResult && (
        <div className="bs-overlay">
          {roundResult.winner === "me" && <Confetti />}
          <div className={`bs-card ${roundResult.winner === "me" ? "win" : "lose"}`}>
            <div className="bs-card-glow" />
            <p className="bs-card-eyebrow" style={{ color: roundResult.winner === "me" ? "#ffd34d" : "#ff8095" }}>
              ROUND {currentRound} {roundResult.winner === "me" ? "WIN" : "LOSS"}
            </p>
            <h2 className="bs-card-title" style={{ color: roundResult.winner === "me" ? "#ffd34d" : "#f2f3f7" }}>
              {roundResult.winner === "me" ? "🏆 You won this round!" : "😤 Opponent wins this round!"}
            </h2>
            <div style={{ display: "flex", justifyContent: "center", gap: 30, margin: "16px 0" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 900, color: "#35c4ff" }}>{roundResult.myScore}</div>
                <div style={{ fontSize: 11, color: "#8d92a3" }}>YOU</div>
              </div>
              <div style={{ fontSize: 24, color: "#8d92a3", alignSelf: "center" }}>vs</div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 900, color: "#ff6fb0" }}>{roundResult.oppScore}</div>
                <div style={{ fontSize: 11, color: "#8d92a3" }}>{oppName?.split(" ")[0]}</div>
              </div>
            </div>
            <p className="bs-card-sub">Next round starting in 3 seconds...</p>
          </div>
        </div>
      )}

      {/* GAME OVER OVERLAY */}
      {gameOver && (
        <div className="bs-overlay">
          {scores.me > scores.opp && <Confetti />}
          <div className={`bs-card ${scores.me > scores.opp ? "win" : "lose"}`}>
            <div className="bs-card-glow" />
            <p className="bs-card-eyebrow" style={{ color: scores.me > scores.opp ? "#ffd34d" : "#ff8095" }}>GAME OVER</p>
            <h2 className="bs-card-title gold">
              {scores.me > scores.opp ? "🏆 You Win!" : scores.me === scores.opp ? "🤝 Draw!" : "😤 You Lost!"}
            </h2>
            <div style={{ display: "flex", justifyContent: "center", gap: 30, margin: "16px 0" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 40, fontWeight: 900, color: "#35c4ff" }}>{scores.me}</div>
                <div style={{ fontSize: 11, color: "#8d92a3" }}>YOU</div>
              </div>
              <div style={{ fontSize: 24, color: "#8d92a3", alignSelf: "center" }}>vs</div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 40, fontWeight: 900, color: "#ff6fb0" }}>{scores.opp}</div>
                <div style={{ fontSize: 11, color: "#8d92a3" }}>{oppName?.split(" ")[0]}</div>
              </div>
            </div>
            {gameOver.gt_change !== 0 && (
              <div style={{ fontSize: 24, fontWeight: 900, color: gameOver.gt_change > 0 ? "#ffd34d" : "#ef4444", margin: "8px 0" }}>
                {gameOver.gt_change > 0 ? "+" : ""}{gameOver.gt_change} GT
              </div>
            )}
            <div className="bs-card-row">
              <button className="bs-btn ghost" onClick={() => go("duo_play")}>Menu</button>
              {onRematch && <button className="bs-btn gold" onClick={onRematch}>⚔️ Rematch!</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS_TEXT = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,800&display=swap');
.bs-stage{min-height:100vh;width:100%;background:radial-gradient(900px 500px at 50% -10%,rgba(60,80,160,.18),transparent 60%),radial-gradient(700px 420px at 80% 110%,rgba(160,60,140,.10),transparent 60%),#0b0c10;display:flex;align-items:flex-start;justify-content:center;font-family:'DM Sans','SF Pro Rounded',ui-rounded,'Segoe UI',system-ui,sans-serif;color:#f2f3f7;-webkit-tap-highlight-color:transparent;user-select:none;padding:14px 10px 30px;}
.bs-shell{width:100%;max-width:480px;display:flex;flex-direction:column;gap:12px}
.bs-top{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:8px}
.bs-lives{display:flex;gap:2px;font-size:1.05rem}
.bs-level{justify-self:center;text-align:center;background:linear-gradient(180deg,#262a38,#171a24);border:1px solid rgba(255,255,255,.12);border-radius:16px;padding:6px 22px;box-shadow:0 6px 18px rgba(0,0,0,.5),inset 0 1px 0 rgba(255,255,255,.10);}
.bs-level-eyebrow{display:block;font-size:.58rem;letter-spacing:3px;color:#8d92a3;font-weight:800}
.bs-level-num{display:block;font-size:1.45rem;font-weight:800;line-height:1.05;background:linear-gradient(180deg,#fff,#bfc6ff);-webkit-background-clip:text;background-clip:text;color:transparent}
.bs-clock{justify-self:end;font-weight:800;font-size:.95rem;color:#aeb3c2;font-variant-numeric:tabular-nums}
.bs-clock.low{color:#ff5566;animation:bsPulse 1s infinite}
@keyframes bsPulse{50%{opacity:.45}}
.bs-timer{height:10px;border-radius:99px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.08);overflow:hidden}
.bs-timer i{display:block;height:100%;border-radius:99px;background:linear-gradient(90deg,#35c4ff,#1473e6);transition:width 1s linear,background .4s}
.bs-timer i.low{background:linear-gradient(90deg,#ff7a45,#ff3355);box-shadow:0 0 14px rgba(255,60,90,.55)}
.bs-board{display:flex;flex-wrap:wrap;justify-content:center;align-items:flex-end;gap:18px 12px;padding:30px 8px 18px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.07);border-radius:24px;min-height:300px;}
.bs-tube{--ball:42px;--cap:4;width:54px;height:calc(var(--cap)*(var(--ball)+5px)+16px);background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.015));border:3px solid rgba(255,255,255,.38);border-top:none;border-radius:0 0 27px 27px;display:flex;flex-direction:column-reverse;align-items:center;gap:5px;padding:6px 0;cursor:pointer;position:relative;transition:transform .15s ease,border-color .2s,box-shadow .25s;box-shadow:inset 0 -14px 22px rgba(0,0,0,.35);}
.bs-tube:active{transform:scale(.97)}
.bs-rim{position:absolute;top:-3px;left:-3px;right:-3px;height:3px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.5) 25%,rgba(255,255,255,.5) 75%,transparent)}
.bs-tube.sel{border-color:#ffd34d;box-shadow:0 0 0 2px rgba(255,211,77,.35),0 0 26px rgba(255,211,77,.35),inset 0 -14px 22px rgba(0,0,0,.35);}
.bs-tube.done{border-color:rgba(90,255,160,.75);box-shadow:0 0 0 2px rgba(90,255,160,.25),0 0 24px rgba(60,255,150,.30),inset 0 -14px 22px rgba(0,0,0,.35);}
.bs-ball{width:var(--ball);height:var(--ball);border-radius:50%;flex:none;position:relative;box-shadow:inset 0 -9px 13px rgba(0,0,0,.38),0 3px 6px rgba(0,0,0,.45);transition:transform .18s ease;}
.bs-ball::after{content:"";position:absolute;top:12%;left:17%;width:36%;height:27%;border-radius:50%;background:radial-gradient(closest-side,rgba(255,255,255,.95),rgba(255,255,255,0));transform:rotate(-18deg);}
.bs-ball.lift{transform:translateY(calc(-1*var(--ball) - 14px)) scale(1.04);filter:drop-shadow(0 6px 10px rgba(0,0,0,.5))}
.bs-ball.drop{animation:bsDrop .3s cubic-bezier(.3,1.4,.5,1)}
@keyframes bsDrop{0%{transform:translateY(calc(-2.4*var(--ball)))}72%{transform:translateY(4px) scaleY(.92)}100%{transform:translateY(0) scaleY(1)}}
.bs-bottom{display:flex;align-items:center;justify-content:space-between;gap:10px}
.bs-undo{font:inherit;font-weight:800;color:#f2f3f7;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.14);border-radius:14px;padding:10px 18px;cursor:pointer;}
.bs-undo:disabled{opacity:.35;cursor:default}
.bs-meta{display:flex;align-items:center;gap:14px;font-weight:700;font-size:.85rem;color:#8d92a3}
.bs-overlay{position:fixed;inset:0;z-index:60;display:flex;align-items:center;justify-content:center;background:rgba(8,9,14,.78);backdrop-filter:blur(4px);padding:20px;overflow:hidden;}
.bs-overlay.red{background:rgba(26,8,12,.82)}
.bs-card{position:relative;width:100%;max-width:360px;text-align:center;background:linear-gradient(180deg,#23252f,#15161d);border:1px solid rgba(255,255,255,.12);border-radius:26px;padding:34px 26px 28px;box-shadow:0 30px 70px rgba(0,0,0,.6);animation:bsPop .4s cubic-bezier(.2,1.4,.4,1);}
@keyframes bsPop{0%{transform:scale(.8) translateY(16px);opacity:0}100%{transform:scale(1) translateY(0);opacity:1}}
.bs-card.lose{border-color:rgba(255,80,100,.35);background:linear-gradient(180deg,#2c1a20,#16121a)}
.bs-card-glow{position:absolute;inset:-1px;border-radius:26px;pointer-events:none;box-shadow:0 0 60px rgba(255,211,77,.22) inset}
.bs-card-eyebrow{font-size:.62rem;letter-spacing:3.5px;font-weight:800;color:#8d92a3;margin:0 0 6px}
.bs-card-title{font-size:1.85rem;font-weight:800;margin:0 0 14px;color:#f2f3f7}
.bs-card-title.gold{background:linear-gradient(180deg,#fff2c4,#ffd34d 55%,#e8a400);-webkit-background-clip:text;background-clip:text;color:transparent;}
.bs-card-sub{color:#aab0c0;font-weight:600;font-size:.9rem;margin:0 0 20px}
.bs-card-row{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}
.bs-btn{font:inherit;font-weight:800;font-size:1rem;border:none;border-radius:16px;padding:13px 26px;cursor:pointer;transition:transform .12s,filter .15s;}
.bs-btn:active{transform:scale(.96)}
.bs-btn.gold{color:#3a2c00;background:linear-gradient(180deg,#ffe9a0,#ffce3c);box-shadow:0 8px 22px rgba(255,200,40,.35),inset 0 1px 0 rgba(255,255,255,.7);}
.bs-btn.ghost{color:#f2f3f7;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.16)}
.bs-btn:hover{filter:brightness(1.06)}
.bs-confetti{position:absolute;inset:0;pointer-events:none;overflow:hidden}
.bs-confetti i{position:absolute;top:-20px;display:block;animation:bsFall linear infinite;}
@keyframes bsFall{0%{transform:translateY(-30px) rotate(0deg)}100%{transform:translateY(110vh) rotate(540deg)}}
@media (max-width:430px){.bs-tube{--ball:36px;width:47px}}
@media (max-width:370px){.bs-tube{--ball:31px;width:41px}}
`;